import React from "react";
import { Platform, View } from "react-native";
import {
  VictoryAxis,
  VictoryChart,
  VictoryGroup,
  VictoryArea,
  VictoryLabel,
  VictoryContainer,
} from "victory-native";
import { SvgLegend, getLegendLayout, SvgLegendItem } from "./SvgLegend";

type ExportDataset = {
  data: { x: any; y: number; key?: string }[];
  color?: string; // optional
};

type Props = {
  chartId: string;
  width: number;
  datasets: ExportDataset[];
  legendItems: SvgLegendItem[];
  yMax?: number;
  yAxisLabel?: string;
};

export const ExportLineChartSvg: React.FC<Props> = ({
  chartId,
  width,
  datasets,
  legendItems,
  yMax,
  yAxisLabel,
}) => {
  const hasData = Array.isArray(datasets) && datasets.length > 0;

  // Compute yMax safely if not provided
  const computedMax =
    yMax ??
    (hasData
      ? Math.max(
        ...datasets.flatMap((ds) => ds.data.map((d) => Number(d.y) || 0))
      )
      : 0);

  const PLOT_H = 360;

  // Legend area for export (shows ALL legends, wrapped)
  const legendLayout = getLegendLayout(legendItems, width - 80, 12, 10, 18, 3);
  const legendH = legendLayout.height;

  const height = PLOT_H + legendH + 30;

  // Extra left space when a y-axis label is present
  const padding = { top: 20, left: yAxisLabel ? 85 : 70, right: 30, bottom: 80 + legendH };
  return (
    <View
      {...(Platform.OS === "web"
        ? ({ id: `${chartId}` } as any) // ✅ stable DOM id
        : {})}
    >
      <VictoryChart
        width={width}
        height={height}
        padding={padding}
        domainPadding={{ x: 20, y: 20 }}
        domain={{ y: [0, Math.max(10, computedMax + 10)] }}
        containerComponent={
          <VictoryContainer />
        }
      >
        {/* X Axis */}
        <VictoryAxis
          tickLabelComponent={<VictoryLabel angle={45} />}
          style={{
            tickLabels: { fontSize: 10, padding: 12 },
          }}
        />

        {/* Y Axis */}
        <VictoryAxis
          dependentAxis
          label={yAxisLabel}
          style={{
            tickLabels: { fontSize: 10, padding: 8 },
            axisLabel: { fontSize: 12, padding: 50 },
          }}
        />

        {/* Series */}
        <VictoryGroup style={{ data: { fillOpacity: 0.25 } }}>
          {hasData &&
            datasets.map((ds, idx) => {
              const c = ds.color ?? "#4BB75F";
              return (
                <VictoryArea
                  key={idx}
                  interpolation="natural"
                  data={ds.data}
                  style={{ data: { stroke: c, fill: c } }}
                />
              );
            })}
        </VictoryGroup>

        {/* Wrapped legend (all items) */}
        {legendItems.length > 0 && (
          <SvgLegend
            x={40}
            y={height - legendH - 15}
            width={width - 80}
            items={legendItems}
            maxColumns={3}
          />
        )}
      </VictoryChart>

    </View>
  );
};
